import {
  BeamButtonProps,
  Button,
  Chips,
  ChipValue,
  Css,
  GridCellContent,
  Palette,
  Tag,
  TagType,
} from "@homebound/beam";
import get from "lodash/get";
import isString from "lodash/isString";
import { useState } from "react";

import { Maybe } from "src/generated/graphql-types";
import { centsToDollars } from "src/utils";

import { DateOnly } from "src/utils/dates";
import { formatDate, FormattedDate, FormattedDateProps } from "./FormattedDate";
import { Percentage } from "./Percentage";
import { Price, PriceProps } from "./Price";

export const emptyCellDash = "-";

// Body cells - All should support an `emptyCell`/`&mdash;` fallback for null/undefined/empty string values. "0" should be shown
export function priceCell(props: PriceProps & { onClick?: () => void }) {
  const { onClick, ...priceProps } = props;
  return {
    // alignment: "right",
    content: () =>
      // Only include the Link if there is a value for price. The link on the cell has been mostly used for the Budget table, where it doesn't make sense to have a link.
      onClick && typeof props.valueInCents === "number" ? (
        <button {...{ onClick }} color={Palette.Blue700}>
          <Price {...priceProps} />
        </button>
      ) : (
        <Price {...priceProps} />
      ),
    value:
      `${!props.valueInCents ? "" : props.valueInCents < 0 ? "-$" : "+$"}` +
      centsToDollars(Math.abs(props.valueInCents ?? 0)),
    sortValue: props.valueInCents,
  };
}

export function dateCell(
  date: Date | DateOnly | undefined | null,
  props: Pick<FormattedDateProps<any>, "dateFormatStyle" | "timeFormatStyle" | "xss"> = {},
) {
  return {
    content: () => <FormattedDate date={date || undefined} {...props} mdash />,
    value: date ? formatDate(date) : "",
    sortValue: date?.valueOf() || 0,
  };
}

export function chipCell(
  values: string[] | ChipValue[],
  displayLimit?: number,
  onMoreClick?: BeamButtonProps["onClick"],
) {
  // Normalize to ChipValues
  const chipValues: ChipValue[] = (
    values.length > 0 && isString(values[0])
      ? values.map((v) => ({ text: v as string, title: v as string }))
      : (values as ChipValue[])
  ).uniqueByKey("title");

  function ChipCellContent() {
    const [showAll, setShowAll] = useState(false);
    const totalChips = chipValues.uniqueByKey("title");
    const limitedChips = displayLimit && !showAll ? totalChips.slice(0, displayLimit) : totalChips;
    const hiddenChips = totalChips.length - limitedChips.length;

    return chipValues.length > 0 ? (
      <div>
        <Chips values={limitedChips} xss={Css.mt0.$} />
        {hiddenChips > 0 && !showAll && (
          <Button
            variant="text"
            label={`+${hiddenChips} more`}
            onClick={onMoreClick ? onMoreClick : () => setShowAll(true)}
          />
        )}
      </div>
    ) : (
      <>{emptyCellDash}</>
    );
  }

  return {
    content: () => <ChipCellContent />,
    value: chipValues.map((v) => `${get(v, ["text"])},${get(v, ["title"])}`).join(),
  };
}

export function percentageCell(percent: Maybe<number>, id?: string): GridCellContent {
  return { alignment: "right", content: () => <Percentage {...{ percent, id }} />, value: percent ?? 0 };
}

export function yesNoCell(value: boolean): GridCellContent {
  return { content: () => (value ? "Yes" : "No") };
}

export function tagCell(type: TagType, text: string): GridCellContent {
  return {
    content: () => (
      <div>
        <Tag type={type} text={text} />
      </div>
    ),
    value: text,
  };
}

export function preserveLinesCell(value: string) {
  const formattedText = value.split("\n\n").map((p, i) => (
    <p key={i} css={Css.mb2.$}>
      {p.split("\n").map((sentence, j) => (
        <span key={j}>
          {sentence}
          <br />
        </span>
      ))}
    </p>
  ));
  return { alignment: "left", content: value.length > 0 ? <div>{formattedText}</div> : emptyCellDash, value };
}

// Footer/Total cells -
export function priceTotal(props: PriceProps): GridCellContent {
  return {
    alignment: "right",
    content: () => <Price dropZero {...props} />,
    value:
      `${!props.valueInCents ? "" : props.valueInCents < 0 ? "-$" : "+$"}` +
      centsToDollars(Math.abs(props.valueInCents ?? 0)),
    sortValue: props.valueInCents,
  };
}
